import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Bell,
  Target,
  Calendar,
  Droplets,
  RefreshCw,
  Smartphone
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#0891b2';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#0891b2';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Target className="w-5 h-5" />,
      question: "How do I set my daily hydration goal?",
      answer: (
        <div className="space-y-2">
          <p>To set your daily hydration goal:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Hydrate Daily</li>
            <li>Tap on <strong>"Set Daily Goal"</strong> button</li>
            <li>Use the slider to adjust your goal (1000ml - 5000ml)</li>
            <li>Tap <strong>"Save Goal"</strong> to confirm</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Tip: The recommended daily water intake is 2000-3000ml for most adults, but this varies based on your activity level, climate, and health conditions.
          </p>
        </div>
      )
    },
    {
      icon: <Droplets className="w-5 h-5" />,
      question: "How do I log my water intake?",
      answer: (
        <div className="space-y-2">
          <p>There are several quick ways to log water:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Glass (250ml):</strong> Tap the glass icon for a standard cup</li>
            <li><strong>Bottle (500ml):</strong> Tap the bottle icon for a water bottle</li>
            <li><strong>Large (1000ml):</strong> Tap the large icon for a liter</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Each tap adds the specified amount to your daily total. Your progress is shown in the circular progress indicator.
          </p>
        </div>
      )
    },
    {
      icon: <Bell className="w-5 h-5" />,
      question: "How do I set up hydration reminders?",
      answer: (
        <div className="space-y-2">
          <p>To enable hydration reminders:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Tap the <strong>gear icon</strong> in the top left corner</li>
            <li>Toggle on <strong>"Reminders"</strong></li>
            <li>Grant notification permissions when prompted</li>
            <li>Customize the reminder interval (30 min to 4 hours)</li>
            <li>Set your active hours (when reminders should be sent)</li>
          </ol>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Reminders will only be sent during your specified active hours to avoid disturbances during sleep.
          </p>
        </div>
      )
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      question: "How can I view my hydration history?",
      answer: (
        <div className="space-y-2">
          <p>To view your hydration history:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Tap <strong>"View History"</strong> on the main screen</li>
            <li>Use the arrows to navigate between months</li>
            <li>Tap on any day to see detailed information</li>
          </ol>
          <p className="mt-3"><strong>Color indicators:</strong></p>
          <ul className="list-disc pl-5 space-y-1">
            <li><span style={{ color: '#22c55e' }}>Green</span>: Goal achieved</li>
            <li><span style={{ color: '#3b82f6' }}>Blue</span>: Partial progress</li>
            <li><span style={{ color: '#9ca3af' }}>Gray</span>: No data recorded</li>
          </ul>
        </div>
      )
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      question: "How does the streak system work?",
      answer: (
        <div className="space-y-2">
          <p>The streak system tracks consecutive days you meet your hydration goal:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your streak increases by 1 each day you reach 100% of your goal</li>
            <li>Missing your goal for a day resets your streak to 0</li>
            <li>Your current streak is displayed at the top of the main screen</li>
            <li>Your longest streak ever is shown in the History statistics</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Streaks are a great way to build healthy hydration habits!
          </p>
        </div>
      )
    },
    {
      icon: <Smartphone className="w-5 h-5" />,
      question: "Will my data sync across devices?",
      answer: (
        <div className="space-y-2">
          <p>Currently, Hydrate Daily stores all data locally on your device:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Data is <strong>not synced</strong> across devices</li>
            <li>Uninstalling the app will delete all your data</li>
            <li>iOS device backups may include app data depending on your backup settings</li>
          </ul>
          <p className="mt-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            This design choice prioritizes your privacy by keeping all data on your device with no cloud connections.
          </p>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Hydrate Daily" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Hydrate%20Daily%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Hydrate Daily
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Hydrate Daily</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlab</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 16.0+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Health & Fitness</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/hydratedaily/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/hydratedaily/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms & Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
